import type { FilterSection } from './types';
import type { FetchShortsOptions } from '~/composables/shorts/useFetchShorts';
import type { FeatureEnum } from '~/core/entities/CRMEnums';
import type { ShortsFilters } from '~/core/entities/property/filters';
import type { LocationQuery } from 'vue-router';

import { useFeedState } from '~/composables/shorts';
import {
	PropertyTypeEnum,
	propertyTypesSlugByValue,
	propertyTypesValueBySlug
} from '~/core/entities/CRMEnums';

export interface ExploreFilters {
	macroregion?: string;
	cities: string[];
	regions: string[];
	propertyTypes: PropertyTypeEnum[];
	minPrice?: number;
	maxPrice?: number;
	minArea?: number;
	maxArea?: number;
	bedrooms?: number;
	suites?: number;
	parkingSpots?: number;
	features: FeatureEnum[];
}

export interface ExploreFiltersQuery {
	[key: string]: string | undefined;
	macroregions?: string;
	cities?: string;
	regions?: string;
	propertyTypes?: string;
	minAskingPrice?: string;
	maxAskingPrice?: string;
	minArea?: string;
	maxArea?: string;
	bedrooms?: string;
	features?: string;
}

interface UseExploreFiltersOptions {
	getBaseFilters?: (context: {
		hasActiveFilters: boolean;
	}) => Partial<ShortsFilters>;
	getFetchOptions?: (context: {
		hasActiveFilters: boolean;
	}) => FetchShortsOptions;
}

function createDefaultFilters(): ExploreFilters {
	return {
		macroregion: undefined,
		cities: [],
		regions: [],
		propertyTypes: [],
		minPrice: undefined,
		maxPrice: undefined,
		minArea: undefined,
		maxArea: undefined,
		bedrooms: undefined,
		suites: undefined,
		parkingSpots: undefined,
		features: []
	};
}

export function useExploreFilters(options: UseExploreFiltersOptions = {}) {
	const filters = useState<ExploreFilters>(
		'exploreFilters',
		createDefaultFilters
	).value;
	const isFilterDropdownOpen = useState(
		'exploreFilterDropdownOpen',
		() => false
	);

	const { applyFilters: applyFeedFilters } = useFeedState();
	let sourcePropertyTypesQuery: string | undefined;
	let sourcePropertyTypes: PropertyTypeEnum[] = [];

	const hasActiveFilters = computed(
		() =>
			filters.macroregion !== undefined ||
			filters.cities.length > 0 ||
			filters.regions.length > 0 ||
			filters.propertyTypes.length > 0 ||
			filters.features.length > 0 ||
			filters.minPrice !== undefined ||
			filters.maxPrice !== undefined ||
			filters.minArea !== undefined ||
			filters.maxArea !== undefined ||
			filters.bedrooms !== undefined ||
			filters.suites !== undefined ||
			filters.parkingSpots !== undefined
	);
	function setMacroregion(macroregion: string, autoApply = true) {
		if (filters.macroregion === macroregion) {
			filters.macroregion = undefined;
		} else {
			filters.macroregion = macroregion;
			filters.cities = [];
			filters.regions = [];
		}
		if (autoApply) {
			applyFilters();
		}
	}

	function toggleCity(city: string, autoApply = true) {
		const idx = filters.cities.indexOf(city);
		if (idx >= 0) {
			filters.cities.splice(idx, 1);
		} else {
			filters.cities.push(city);
		}
		if (autoApply) {
			applyFilters();
		}
	}

	function toggleRegion(region: string, autoApply = true) {
		const idx = filters.regions.indexOf(region);
		if (idx >= 0) {
			filters.regions.splice(idx, 1);
		} else {
			filters.regions.push(region);
		}
		if (autoApply) {
			applyFilters();
		}
	}

	function togglePropertyType(type: PropertyTypeEnum, autoApply = true) {
		sourcePropertyTypesQuery = undefined;
		sourcePropertyTypes = [];
		const idx = filters.propertyTypes.indexOf(type);
		if (idx >= 0) {
			filters.propertyTypes.splice(idx, 1);
		} else {
			filters.propertyTypes.push(type);
		}
		if (autoApply) {
			applyFilters();
		}
	}

	function toggleFeature(feature: FeatureEnum, autoApply = true) {
		const idx = filters.features.indexOf(feature);
		if (idx >= 0) {
			filters.features.splice(idx, 1);
		} else {
			filters.features.push(feature);
		}
		if (autoApply) {
			applyFilters();
		}
	}

	function setPriceRange(min?: number, max?: number, autoApply = true) {
		const isSame = filters.minPrice === min && filters.maxPrice === max;
		filters.minPrice = isSame ? undefined : min;
		filters.maxPrice = isSame ? undefined : max;
		if (autoApply) {
			applyFilters();
		}
	}

	function setAreaRange(min?: number, max?: number, autoApply = true) {
		const isSame = filters.minArea === min && filters.maxArea === max;
		filters.minArea = isSame ? undefined : min;
		filters.maxArea = isSame ? undefined : max;
		if (autoApply) {
			applyFilters();
		}
	}

	function setRoomValue(
		field: 'bedrooms' | 'suites' | 'parkingSpots',
		value?: number,
		autoApply = true
	) {
		if (value === undefined) {
			filters[field] = undefined;
		} else {
			filters[field] = filters[field] === value ? undefined : value;
		}
		if (autoApply) {
			applyFilters();
		}
	}

	function clearAll(autoApply = true) {
		sourcePropertyTypesQuery = undefined;
		sourcePropertyTypes = [];
		filters.macroregion = undefined;
		filters.cities = [];
		filters.regions = [];
		filters.propertyTypes = [];
		filters.minPrice = undefined;
		filters.maxPrice = undefined;
		filters.minArea = undefined;
		filters.maxArea = undefined;
		filters.bedrooms = undefined;
		filters.suites = undefined;
		filters.parkingSpots = undefined;
		filters.features = [];
		if (autoApply) {
			applyFilters();
		}
	}

	function applyFilters(): void {
		applyFiltersAsync().catch(() => {});
	}

	function getFilterCount(section: FilterSection): number {
		switch (section) {
			case 'city':
				return (filters.macroregion ? 1 : 0) + filters.cities.length;
			case 'region':
				return filters.regions.length;
			case 'type':
				return filters.propertyTypes.length;
			case 'price':
				return filters.minPrice !== undefined || filters.maxPrice !== undefined
					? 1
					: 0;
			case 'area':
				return filters.minArea !== undefined || filters.maxArea !== undefined
					? 1
					: 0;
			case 'rooms':
				return [filters.bedrooms, filters.suites, filters.parkingSpots].filter(
					v => v !== undefined
				).length;
			case 'features':
				return filters.features.length;
			default:
				return 0;
		}
	}

	function setDropdownOpen(open: boolean) {
		isFilterDropdownOpen.value = open;
	}

	function reset() {
		clearAll(false);
		isFilterDropdownOpen.value = false;
	}

	async function applyFiltersAsync(): Promise<void> {
		const hasFiltersApplied = hasActiveFilters.value;
		const baseFilters =
			options.getBaseFilters?.({ hasActiveFilters: hasFiltersApplied }) ?? {};
		const fetchOptions =
			options.getFetchOptions?.({ hasActiveFilters: hasFiltersApplied }) ?? {};

		return applyFeedFilters(
			{
				macroregions: filters.macroregion ? [filters.macroregion] : undefined,
				cities: filters.cities.length ? filters.cities : undefined,
				regions: filters.regions.length ? filters.regions : undefined,
				propertyTypes: filters.propertyTypes.length
					? filters.propertyTypes
					: undefined,
				minAskingPrice: filters.minPrice,
				maxAskingPrice: filters.maxPrice,
				minArea: filters.minArea,
				maxArea: filters.maxArea,
				bedrooms: filters.bedrooms,
				suites: filters.suites,
				parkingSpots: filters.parkingSpots,
				...baseFilters
			},
			fetchOptions
		);
	}

	function toQueryParams(): ExploreFiltersQuery {
		const query: ExploreFiltersQuery = {};

		if (filters.macroregion) {
			query.macroregions = filters.macroregion;
		}
		if (filters.cities.length > 0) {
			query.cities = filters.cities.join(',');
		}
		if (filters.regions.length > 0) {
			query.regions = filters.regions.join(',');
		}
		if (filters.propertyTypes.length > 0) {
			const stillMatchesSource =
				sourcePropertyTypesQuery !== undefined &&
				sourcePropertyTypes.length === filters.propertyTypes.length &&
				sourcePropertyTypes.every(
					(type, index) => filters.propertyTypes[index] === type
				);
			const propertyTypesQuery = stillMatchesSource
				? sourcePropertyTypesQuery
				: filters.propertyTypes
						.map(type => propertyTypesSlugByValue[type])
						.filter(Boolean)
						.join(',');
			if (propertyTypesQuery) query.propertyTypes = propertyTypesQuery;
		}
		if (filters.minPrice !== undefined) {
			query.minAskingPrice = String(filters.minPrice);
		}
		if (filters.maxPrice !== undefined) {
			query.maxAskingPrice = String(filters.maxPrice);
		}
		if (filters.minArea !== undefined) {
			query.minArea = String(filters.minArea);
		}
		if (filters.maxArea !== undefined) {
			query.maxArea = String(filters.maxArea);
		}
		if (filters.bedrooms !== undefined) {
			query.bedrooms = String(filters.bedrooms);
		}
		if (filters.features.length > 0) {
			query.features = filters.features.join(',');
		}

		return query;
	}

	function fromQueryParams(query: LocationQuery, shouldApply = true): void {
		clearAll(false);

		if (query.macroregions && typeof query.macroregions === 'string') {
			filters.macroregion = query.macroregions;
		}
		if (query.cities && typeof query.cities === 'string') {
			filters.cities = query.cities.split(',').filter(Boolean);
		}

		if (query.regions && typeof query.regions === 'string') {
			filters.regions = query.regions.split(',').filter(Boolean);
		}

		if (query.propertyTypes && typeof query.propertyTypes === 'string') {
			const propertyTypeValues = new Set(Object.values(PropertyTypeEnum));
			const parsedPropertyTypes = query.propertyTypes
				.split(',')
				.filter(Boolean)
				.map(sourceValue => ({
					sourceValue,
					propertyType:
						propertyTypesValueBySlug[
							sourceValue as keyof typeof propertyTypesValueBySlug
						] ??
						(propertyTypeValues.has(sourceValue as PropertyTypeEnum)
							? (sourceValue as PropertyTypeEnum)
							: undefined)
				}))
				.filter(
					(
						entry
					): entry is {
						sourceValue: string;
						propertyType: PropertyTypeEnum;
					} => entry.propertyType !== undefined
				);
			filters.propertyTypes = parsedPropertyTypes.map(
				entry => entry.propertyType
			);
			sourcePropertyTypesQuery =
				parsedPropertyTypes.map(entry => entry.sourceValue).join(',') ||
				undefined;
			sourcePropertyTypes = [...filters.propertyTypes];
		}

		if (query.minAskingPrice && typeof query.minAskingPrice === 'string') {
			const val = parseInt(query.minAskingPrice, 10);
			if (!isNaN(val)) filters.minPrice = val;
		}

		if (query.maxAskingPrice && typeof query.maxAskingPrice === 'string') {
			const val = parseInt(query.maxAskingPrice, 10);
			if (!isNaN(val)) filters.maxPrice = val;
		}

		if (query.minArea && typeof query.minArea === 'string') {
			const val = parseInt(query.minArea, 10);
			if (!isNaN(val)) filters.minArea = val;
		}

		if (query.maxArea && typeof query.maxArea === 'string') {
			const val = parseInt(query.maxArea, 10);
			if (!isNaN(val)) filters.maxArea = val;
		}

		if (query.bedrooms && typeof query.bedrooms === 'string') {
			const val = parseInt(query.bedrooms, 10);
			if (!isNaN(val)) filters.bedrooms = val;
		}

		if (query.features && typeof query.features === 'string') {
			filters.features = query.features
				.split(',')
				.filter(Boolean) as FeatureEnum[];
		}

		if (shouldApply) {
			applyFilters();
		}
	}

	return {
		filters: readonly(filters),
		hasActiveFilters,
		isFilterDropdownOpen: readonly(isFilterDropdownOpen),
		setMacroregion,
		toggleCity,
		toggleRegion,
		togglePropertyType,
		toggleFeature,
		setPriceRange,
		setAreaRange,
		setRoomValue,
		clearAll,
		getFilterCount,
		setDropdownOpen,
		applyFilters,
		applyFiltersAsync,
		reset,
		toQueryParams,
		fromQueryParams
	};
}
